import { useState, useCallback, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import OptimizedImage from "@/components/common/OptimizedImage";
import LazyLoadBoundary from "@/components/common/LazyLoadBoundary";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import ImageModal from "./ImageModal";

interface ItemImage {
  id: string;
  item_id: string;
  url: string;
  position: number;
  is_main?: boolean;
}

interface ImageGalleryProps {
  images: ItemImage[];
  title: string;
  onImageError?: () => void;
  navigationStyle?: "thumbnails" | "overlayIndicators";
}

export default function ImageGallery({
  images,
  title,
  onImageError,
  navigationStyle = "overlayIndicators",
}: ImageGalleryProps) {
  // Ensure images is an array and filter out any invalid images
  const validImages = (images || []).filter((img) => img && img.url);

  // Find the main image index or default to 0
  const mainImageIndex = validImages.findIndex((img) => img.is_main);
  const initialImageIndex = mainImageIndex >= 0 ? mainImageIndex : 0;
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Image navigation handlers
  const nextImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!validImages.length) return;
      setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
    },
    [validImages.length],
  );

  const prevImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!validImages.length) return;
      setCurrentImageIndex(
        (prev) => (prev - 1 + validImages.length) % validImages.length,
      );
    },
    [validImages.length],
  );

  const handleImageError = () => {
    setImageError(true);
    onImageError?.();
  };

  // Ensure current index is within bounds
  useEffect(() => {
    if (currentImageIndex >= validImages.length) {
      const timer = setTimeout(() => {
        setCurrentImageIndex(0);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [validImages.length, currentImageIndex]);

  // Get current image safely
  const currentImage = validImages[currentImageIndex];

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        <div className="relative aspect-[4/3] bg-secondary rounded-lg overflow-hidden">
          {currentImage && !imageError ? (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full h-full"
            >
              <OptimizedImage
                src={currentImage.url}
                alt={`${title} - main image`}
                width={1200}
                height={900}
                objectFit="contain"
                className="h-full w-full cursor-pointer"
                priority={true}
                sizes="(max-width: 1024px) 100vw, min(50vw, 1200px)"
                onError={handleImageError}
                usage="detail-main"
              />
            </button>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-8xl font-bold text-text-light">
                {title.charAt(0)}
              </span>
            </div>
          )}

          {/* Navigation Buttons */}
          {validImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-md bg-black/55 p-2 text-white backdrop-blur-sm transition-opacity hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="View previous image"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-md bg-black/55 p-2 text-white backdrop-blur-sm transition-opacity hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="View next image"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {navigationStyle === "overlayIndicators" && (
                <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/35 px-3 py-1.5">
                  {validImages.map((image, index) => (
                    <button
                      key={image.id || index}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      aria-label={`View image ${index + 1}`}
                      className={`h-2.5 w-2.5 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                        index === currentImageIndex
                          ? "bg-surface"
                          : "bg-surface/50 hover:bg-surface/80"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Thumbnail Navigation */}
        {navigationStyle === "thumbnails" && validImages.length > 0 && (
          <div className="pl-1 pt-1 flex gap-2 overflow-x-auto">
            {validImages.map((image, index) => (
              <LazyLoadBoundary key={image.id || index}>
                <button
                  onClick={() => setCurrentImageIndex(index)}
                  className={`relative w-20 h-20 rounded-md overflow-hidden flex-shrink-0 ${
                    index === currentImageIndex
                      ? "ring-2 ring-primary"
                      : "ring-1 ring-border"
                  }`}
                >
                  <OptimizedImage
                    src={image.url}
                    alt={`${title} - image ${index + 1}`}
                    width={80}
                    height={80}
                    objectFit="cover"
                    className="h-full w-full"
                    priority={index === 0}
                    sizes="80px"
                    enableBlurPlaceholder={false}
                    usage="thumbnail"
                  />
                </button>
              </LazyLoadBoundary>
            ))}
          </div>
        )}

        {/* Image Modal */}
        <ImageModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          images={validImages}
          title={title}
          currentImageIndex={currentImageIndex}
          onImageChange={setCurrentImageIndex}
        />
      </div>
    </ErrorBoundary>
  );
}
